<template>
  <div v-if="policyDetails && policyState">
    <RevContainer class="py-12 md:my-24">
      <RevLink
        class="body-2-bold flex items-center gap-8"
        :to="{ name: ROUTE_NAMES.DASHBOARD.SERVICES_HUB }"
        :tracking="{
          zone: TRACKING_ZONES.POLICY_DETAILS,
          name: 'go-back-to-all-policies',
        }"
      >
        <IconArrowLeft />
        {{ i18n(translations.backLabel) }}
      </RevLink>
    </RevContainer>

    <div class="flex justify-center pb-72 md:mx-32 md:gap-32">
      <div class="max-w-[650px] grow space-y-16 md:space-y-32">
        <div
          class="bg-surface-default-low py-24 md:rounded-lg md:overflow-hidden md:pb-0 md:pt-32"
        >
          <div class="px-24 md:px-32">
            <PolicyStateTag :state="policyState" />
            <PolicyAndOrderlineDetails
              :orderline="policyDetails.orderline"
              :policy="policyDetails.policy"
            />

            <SubmitClaim
              v-if="displaySubmitClaim"
              class="mt-24 md:hidden"
              :partner-name="policyDetails.policy.offer.partnerName"
              :policy-state
            />
          </div>
          <CoverageDetails
            :benefits="policyDetails.policy.offer.benefits"
            class="md:mt-32"
            :class="{
              'mt-20': displaySubmitClaim,
              'mt-24': !displaySubmitClaim,
            }"
            :compliancy-documents="
              policyDetails.policy.offer.compliancyDocuments
            "
          />
        </div>

        <div
          class="bg-surface-default-low py-24 md:rounded-lg md:overflow-hidden md:pb-0 md:pt-32"
        >
          <PaymentDetails
            :auto-renew="policyDetails.policy.autoRenew"
            class="px-24 md:px-32"
            :dueAmount
            :end-date="policyDetails.policy.endDate"
            :next-payment="policyDetails.nextPayment"
            :payment-frequency="policyDetails.policy.paymentFrequency"
            :policy-id
            :state="policyState"
          />
          <PaymentHistory
            class="mt-24 md:mt-32"
            :payment-history="policyDetails.paymentHistory"
          />
        </div>

        <div class="px-24 pt-16 md:hidden">
          <HelpAndCancelButtons
            :orderline="policyDetails.orderline"
            :policy="policyDetails.policy"
          />
        </div>
      </div>

      <!-- Only on desktop -->
      <div class="hidden min-w-[438px] space-y-32 md:block">
        <div
          v-if="displaySubmitClaim"
          class="bg-surface-default-low rounded-lg space-y-16 overflow-hidden p-32"
        >
          <div class="heading-3">
            {{ i18n(translations.submitClaimSectionTitle) }}
          </div>
          <SubmitClaim
            :partner-name="policyDetails.policy.offer.partnerName"
            :policy-state="policyDetails.policy.state"
          />
        </div>

        <div
          class="bg-surface-default-low rounded-lg space-y-16 overflow-hidden p-32"
        >
          <div class="heading-3">
            {{ i18n(translations.helpSectionTitle) }}
          </div>
          <HelpAndCancelButtons
            :orderline="policyDetails.orderline"
            :policy="policyDetails.policy"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useAsyncData, useRoute, useRouteParams } from '#imports'
import { computed, onMounted, ref } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevContainer } from '@ds/components/Container'
import { RevLink } from '@ds/components/Link'
import { IconArrowLeft } from '@ds/icons/IconArrowLeft'

import { ROUTE_NAMES } from '@/scopes/dashboard/constants'
import type { PolicyDetails } from '~/scopes/dashboard/services-hub/types'

import PolicyStateTag from '../../components/PolicyStateTag/PolicyStateTag.vue'
import { TRACKING_ZONES } from '../../constant'

import translations from './PolicyDetails.translations'
import CoverageDetails from './components/CoverageDetails/CoverageDetails.vue'
import HelpAndCancelButtons from './components/HelpAndCancelButtons/HelpAndCancelButtons.vue'
import PaymentDetails from './components/PaymentDetails/PaymentDetails.vue'
import PaymentHistory from './components/PaymentHistory/PaymentHistory.vue'
import PolicyAndOrderlineDetails from './components/PolicyAndOrderlineDetails/PolicyAndOrderlineDetails.vue'
import SubmitClaim from './components/SubmitClaim/SubmitClaim.vue'

const { policyId } = useRouteParams()
const $httpFetch = useHttpFetch()
const i18n = useI18n()
const route = useRoute()
const { openSuccessToast } = useTheToast()
const { trackBlockImpression } = useTracking()
const { data: policyDetails } = await useAsyncData(
  `policy-details-${policyId}`,
  () =>
    $httpFetch('/api/insurance/v1/services-hub/policies/:policyId', {
      method: 'GET',
      pathParams: { policyId },
    }),
)
const dueAmount = ref<Price | null>(policyDetails.value?.dueAmount ?? null)
const policyState = ref<PolicyDetails['policy']['state'] | undefined>(
  policyDetails.value?.policy.state,
)

const displaySubmitClaim = computed(() => {
  return policyState.value === 'ACTIVE' || policyState.value === 'ON_HOLD'
})

onMounted(async () => {
  if (policyDetails.value?.policy.state) {
    trackBlockImpression({
      name: 'policy-details',
      state: policyDetails.value.policy.state,
      isCancellable: policyDetails.value.policy.isCancellable,
      hasDueAmount: policyDetails.value.dueAmount !== null,
      paymentFrequency: policyDetails.value.policy.paymentFrequency,
      autoRenew: policyDetails.value.policy.autoRenew,
    })
  }

  const updatePaymentSuccess = route.query.update_payment === 'success'

  if (!updatePaymentSuccess) {
    return
  }

  openSuccessToast({
    title: i18n(translations.updatePaymentMethodSuccessToastTitle),
  })

  const nextQuery = { ...route.query }
  delete nextQuery.update_payment
  navigateTo({ path: route.path, query: nextQuery }, { replace: true })

  dueAmount.value = null
  policyState.value = 'ACTIVE'
})
</script>
